import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useHttp, useToasts } from '@/composables/index.js';

export const usePdfStore = defineStore('pdf', () => {
  const toastStore = useToasts();
  const status = ref('Idle'); // 'Idle', 'Pending', 'Ready', 'Failed'
  const currentPdfId = ref(null);
  const currentPlanId = ref(null);

  const startGeneration = async (planId, previewPath) => {
    if (status.value !== 'Idle') return;

    status.value = 'Pending';
    currentPlanId.value = planId;

    try {
      const response = await useHttp().post(`/plans/pdf/${planId}/generate`, { previewPath });

      currentPdfId.value = response.data.id;

      // Start polling (do not await this, let it run in background)
      setTimeout(() => {
        pollStatus(planId, currentPdfId.value);
      }, 10000);
    } catch (error) {
      console.error('Failed to start generation', error);
      reset();
    }
  };

  const pollStatus = async (planId, pdfId) => {
    // Stop if the user started generating a different pdf or an error occured
    if (pdfId !== currentPdfId.value) return;

    try {
      const response = await useHttp().get('/plans/pdf/generation-status');

      const data = response.data;
      status.value = data.status;

      if (data.status === 'Completed') {
        const downloadResponse = await useHttp().get('/plans/pdf/download', null, null, { responseType: 'blob' });

        if (!(downloadResponse instanceof Blob)) {
          toastStore.error('Failed to retrieve PDF file.');
          status.value = 'Idle';
          return;
        }

        toastStore.success('PDF generated successfully! Opening in new tab...');

        const linkUrl = window.URL.createObjectURL(downloadResponse);

        const link = document.createElement('a');
        link.href = linkUrl;
        link.download =`Plan ${planId}.pdf`;

        link.click();

        setTimeout(() => {
          window.URL.revokeObjectURL(linkUrl);
        }, 2000);

        status.value = 'Idle';
      } else if (data.status === 'Failed') {
        toastStore.danger(`PDF generation failed! ${data}`);
        status.value = 'Idle';
      } else {
        // Still Pending, wait and retry
        setTimeout(() => {
          pollStatus(planId, pdfId);
        }, 15000);
      }
    } catch (error) {
      console.error('Polling error', error);
    }
  };

  const reset = () => {
    status.value = 'Idle';
    currentPdfId.value = null;
  };

  return {
    status,
    startGeneration,
    reset,
  };
});
